import { UploadedFile } from '@komodo-design-system/komodo-core-web-components';
import { UploadProgress, FileUploadResult, FileToUpload, FileUploadUtils } from './fileUploadUtils';
class FileUploadService {
  private setUploadProgress?: (progress: Partial<UploadProgress> | ((prev: UploadProgress) => UploadProgress)) => void;
  private currentProgress: UploadProgress = {
    show: false,
    completed: 0,
    total: 0,
    currentFile: '',
    stage: ''
  };

  constructor(setUploadProgress?: (progress: Partial<UploadProgress> | ((prev: UploadProgress) => UploadProgress)) => void) {
    this.setUploadProgress = setUploadProgress;
  }

  private updateProgress(update: Partial<UploadProgress> | ((prev: UploadProgress) => UploadProgress)) {
    if (this.setUploadProgress) {
      this.setUploadProgress(update);
    }
    
    // Update internal state for tracking
    if (typeof update === 'function') {
      this.currentProgress = update(this.currentProgress);
    } else {
      this.currentProgress = { ...this.currentProgress, ...update };
    }
  }

  private async uploadFilesInBatches(
    files: FileToUpload[], 
    batchSize: number = 12,
    stage: string = 'Uploading'
  ): Promise<{ uploadedCount: number; errorCount: number; results: string[] }> {
    let uploadedCount = 0;
    let errorCount = 0;
    const results: string[] = [];
    
    // Process files in batches to avoid overwhelming the server
    for (let i = 0; i < files.length; i += batchSize) {
      const batch = files.slice(i, i + batchSize);
      
      // Upload all files in current batch concurrently
      const batchPromises = batch.map(async ({ fileData, fileName, displayName }) => {
        try {
          // Update progress for current file
          this.updateProgress({
            currentFile: displayName || fileName,
            stage: stage
          });
          
          const success = await FileUploadUtils.uploadFileToS3(fileData, fileName);
          const name = displayName || fileName;
          // Update progress after file completion
          this.updateProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
          
          if (success) {
            return { success: true, message: `✓ ${name}` };
          }
          return { success: false, message: `✗ ${name} (failed to upload)` };
        } catch (error) {
          console.error(`Error uploading file ${fileName}:`, error);
          
          // Update progress even on error
          this.updateProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
          
          return { success: false, message: `✗ ${displayName || fileName} (upload error)` };
        }
      });
      
      // Wait for current batch to complete before starting next batch
      const batchResults = await Promise.all(batchPromises);
      // Process results
      for (const result of batchResults) {
        if (result.success) {
          uploadedCount++;
        } else {
          errorCount++;
        }
        results.push(result.message);
      }
      
      // Small delay between batches to prevent server overload
      if (i + batchSize < files.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    return { uploadedCount, errorCount, results };
  }

  async processFileUpload(filesList: UploadedFile[], jobId: string, totalFiles: number): Promise<FileUploadResult> {
    try {
      // Calculate total files for progress tracking

      // Categorize files
      const { zipFileObj, excelFiles } = FileUploadUtils.categorizeFiles(filesList);

      let totalUploaded = 0;
      let totalErrors = 0;

      // Step 1: Process ZIP files first
      if (zipFileObj?.fileReference) {
        const zipFilesToUpload = await FileUploadUtils.processZipFile(zipFileObj);

        if (zipFilesToUpload.length > 0) {
          const { uploadedCount, errorCount } = await this.uploadFilesInBatches(
            zipFilesToUpload, 
            12, 
            'Uploading extracted files'
          );
          
          totalUploaded += uploadedCount;
          totalErrors += errorCount;
        }
      }

      if (excelFiles.length > 0) {
        
        const excelFilesToUpload = FileUploadUtils.processExcelFiles(excelFiles, jobId);
        const { uploadedCount, errorCount } = await this.uploadFilesInBatches(
          excelFilesToUpload, 
          12, 
          'Uploading Excel files'
        );
        
        totalUploaded += uploadedCount;
        totalErrors += errorCount;
        console.log(`Excel files upload completed: ${uploadedCount} successful, ${errorCount} failed`);
      }

      // Show completion
      this.updateProgress({
        stage: 'Upload completed!',
        currentFile: `Uploaded ${totalUploaded} files successfully`
      });

      // Hide progress bar after 2 seconds
      setTimeout(() => {
        this.updateProgress({ show: false });
      }, 2000);

      return {
        success: totalErrors === 0,
        uploadedCount: totalUploaded,
        errorCount: totalErrors,
        totalFiles,
        message: `Upload completed! ${totalUploaded} successful, ${totalErrors} failed`
      };

    } catch (error) {
      console.error('Error during upload process:', error);
      this.updateProgress({
        stage: 'Upload failed',
        currentFile: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      // Hide progress bar after error
      setTimeout(() => {
        this.updateProgress({ show: false });
      }, 3000);

      return {
        success: false,
        uploadedCount: 0,
        errorCount: 1,
        totalFiles: 0,
        message: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

export default FileUploadService;
